import React from 'react';
import { Dialog, Tooltip, Flex, IconButton } from '@radix-ui/themes';
import { toast } from 'sonner';
import { observer } from 'mobx-react-lite';
import {
   ContentCopyIcon,
   DeleteIcon,
   DownloadIcon,
   EditOutlinedIcon,
} from 'shared/icons';

import { useNavigate } from 'react-router-dom';
import './cv-actions-menu.scss';
import { useManageCvs } from '../manage-cvs.provider';
import { CandidateCv } from '../types';
import { CvConfirmDialog } from '../cv-confirm-dialog';

type MangerBlogMenuAction = 'DELETE' | 'EMPTY';

const CvActionsMenuView = ({ candidateCv }: { candidateCv: CandidateCv }) => {
   const manageCvs = useManageCvs();
   const navigate = useNavigate();
   const [open, setOpen] = React.useState<boolean>(false);
   const [action, setAction] = React.useState<MangerBlogMenuAction>('EMPTY');

   const canRemove = manageCvs.resumes?.length > 1;
   
   return (
      <Flex className="candidate-cv-actions-menu" gap="1" align="center" justify="center">
         <Tooltip content="Редагувати" side="top">
            <IconButton
               color="blue"
               size="2"
               className="action-trigger"
               highContrast
               onClick={() => {
                  navigate(`/candidate/resume/${candidateCv.id}/edit`);
               }}
            >
               <EditOutlinedIcon width={24} height={24} />
            </IconButton>
         </Tooltip>

         <Tooltip content="Створити копію" side="top">
            <IconButton
               color="blue"
               size="2"
               className="action-trigger"
               onClick={() => {
                  navigate(`/candidate/resume/${candidateCv.id}/copy`);
               }}
            >
               <ContentCopyIcon width={24} height={24} />
            </IconButton>
         </Tooltip>

         <Tooltip content="Завантажити PDF" side="top">
            <IconButton
               color="blue"
               loading={
                  manageCvs.loadCvPdfState.status === 'pending' && candidateCv.id === manageCvs.cvId
               }
               size="2"
               className="action-trigger"
               onClick={() => {
                  manageCvs.loadCvPdf({
                     dto: { id: candidateCv.id, contacts: true },
                     onSuccess: (blob) => {
                        const url = URL.createObjectURL(blob);
                        const link = document.createElement('a');
                        link.href = url;
                        link.download = `${candidateCv.name}.pdf`;
                        link.click();
                        URL.revokeObjectURL(url);
                     },
                     onError: () => {
                        toast.error('Виникла помилка, зверніться в службу підтримки');
                     },
                  });
               }}
            >
               <DownloadIcon width={24} height={24} />
            </IconButton>
         </Tooltip>

         <Tooltip content="Видалити" side="top">
            <IconButton
               disabled={!canRemove}
               color="red"
               size="2"
               className="action-trigger"
               onClick={() => {
                  setOpen(true);
                  setAction('DELETE');
               }}
            >
               <DeleteIcon width={24} height={24} />
            </IconButton>
         </Tooltip>

         <Dialog.Root
            open={open}
            onOpenChange={(o) => {
               if (!o) {
                  setAction('EMPTY');
               }
               setOpen(o);
            }}
         >
            <Dialog.Content
               aria-describedby={undefined}
               onOpenAutoFocus={(e) => e.preventDefault()}
            >
               {
                  {
                     DELETE: (
                        <CvConfirmDialog
                           title="Видалити резюме?"
                           message="Ви хочете видалити це резюме?"
                           loading={manageCvs.deleteCvState.status === 'pending'}
                           onConfirm={() => {
                              manageCvs.deleteCv({
                                 dto: { id: candidateCv.id },
                                 onSuccess: (resp) => {
                                    setOpen(false);
                                    toast.success('Резюме видалено');
                                 },
                                 onError: (err) => {
                                    toast.error(err);
                                 },
                              });
                           }}
                        />
                     ),
                     EMPTY: <></>,
                  }[action]
               }
            </Dialog.Content>
         </Dialog.Root>
      </Flex>
   );
};

export const CvActionsMenu = observer(CvActionsMenuView);
